import { Grid, GridItem } from "@chakra-ui/react";
import { FC, Fragment, useContext, useState } from "react";
import { TopCard } from "./TopCard/TopCard";
import {
  ArrowElbowRightIcon,
  SneakerMoveIcon,
  WarningIcon,
  YinYangIcon,
} from "@phosphor-icons/react";
import { MiddleCard } from "./MiddleCard/MiddleCard";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Tooltip,
} from "recharts";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useEndpoints } from "../../../hooks/useEndpoints";
import { Chart, useChart } from "@chakra-ui/charts";
import style from "./InformationsTab.module.scss";
import Typhography from "../../../shared/Typhography/Typhography";
import { IDetailsTeamResponse } from "../../../models/endpoints/Team";
import dayjs from "dayjs";
import { StressChart } from "./Charts/Charts";
import { TablesRow } from "./TablesRow/TablesRow";
import { ITeam } from "../../../models/Team";
import { SecondRow } from "./SecondRow/SecondRow";
import { InfoDrawerContext } from "../../../providers/InfoDrawer";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import { useInjuryEndpoints } from "../../../hooks/useInjuryEndpoints";
import { IActiveInjurySummary } from "../../../models/injury";
import { AvailabilityCard } from "./AvailabilityCard/AvailabilityCard";
import OverviewHub, { OverviewHubTab } from "./OverviewHub/OverviewHub";
import { useOverviewHub } from "./OverviewHub/useOverviewHub";

const InformationsTab: FC<{
  teamQuery: UseQueryResult<ITeam, Error>;
  creatingPdf: boolean;
  /** Deep-link ai tab di dettaglio dalle card dell'hub (#18). */
  onGoTo?: (tab: OverviewHubTab) => void;
}> = ({ teamQuery, creatingPdf, onGoTo }) => {
  const params = useParams<{ id: string }>();
  const endpoints = useEndpoints();
  const hub = useOverviewHub(params.id);
  const [series, setSeries] = useState<
    IDetailsTeamResponse["averageStressByDays"]
  >([]);
  const { setState } = useContext(InfoDrawerContext);

  const query = useQuery({
    queryKey: ["details-team", params.id],
    queryFn: async () => {
      const { data } = await endpoints.teamDetails(params.id!);
      chart.series = [];
      chart.data = [];
      return data;
    },
  });

  const injuryEndpoints = useInjuryEndpoints();

  const injuriesQuery = useQuery<IActiveInjurySummary[]>({
    queryKey: ["team-injuries", params.id],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getTeamInjuries(params.id!);
      return data;
    },
    enabled: !!params.id,
    staleTime: 1000 * 60 * 5,
  });

  const chart = useChart({
    data: query.data?.averageStressByDays || [],
    series: [
      { name: "mean", label: "valore:" }, // Solo il nome del campo
    ],
  });

  /**
   * Che cosa c'è davvero dietro una "media team".
   *
   * Su una squadra dove ha compilato un atleta su 24, scrivere "media team" è
   * falso: il numero è il dato di quella persona. La riga sotto al titolo dice
   * sempre su quanti atleti poggia, e quando non poggia su nessuno lo dichiara.
   */
  const copertura = (contati?: number) => {
    const totale = query.data?.coverage?.athletes;
    if (typeof contati !== "number" || typeof totale !== "number")
      return "media team";
    if (contati === 0) return "nessun dato";
    /**
     * Il periodo va detto insieme alla copertura.
     *
     * La tab Readiness guarda SOLO oggi, questa guarda gli ultimi 14 giorni:
     * sullo stesso team, nello stesso momento, davano 63,2% su 7 atleti e
     * 69,4% su 8. Due numeri entrambi corretti sotto la stessa etichetta
     * "media team" — indistinguibili per chi legge.
     */
    return contati === totale
      ? `ultimi 14 giorni · ${totale} atleti`
      : `ultimi 14 giorni · ${contati} di ${totale} atleti`;
  };

  /**
   * Copertura del Recupero, con l'avvertenza sull'affidabilità.
   *
   * Un indice calcolato su un fattore solo non è un composito: è quella misura
   * con un altro nome, e può spostarsi di cinquanta punti appena ne arriva una
   * seconda. Se succede alla maggioranza degli atleti la card lo dichiara.
   */
  const coperturaRecupero = () => {
    const base = copertura(query.data?.coverage?.recovery);
    const conDato = query.data?.coverage?.recovery ?? 0;
    const fragili = query.data?.coverage?.recoveryUnaMisura ?? 0;
    if (conDato === 0 || fragili === 0) return base;
    return fragili === conDato
      ? `${base} · da una sola misura`
      : `${base} · ${fragili} da una sola misura`;
  };

  const renderFirstRow = () => {
    return (
      <Grid
        templateRows="repeat(1, 1fr)"
        templateColumns="repeat(9, 1fr)"
        gap={4}
      >
        <GridItem colSpan={3}>
          <TopCard
            creatingPdf={creatingPdf}
            isLoading={query.isPending}
            Icon={SneakerMoveIcon}
            label="Sforzo"
            sublabel={copertura(query.data?.coverage?.effort)}
            percentage={query.data?.averageEffort ?? 0}
            labelCircle={query.data?.averageEffort ?? undefined}
          />
        </GridItem>
        <GridItem colSpan={3}>
          <TopCard
            creatingPdf={creatingPdf}
            isLoading={query.isPending}
            Icon={ArrowElbowRightIcon}
            label="Recupero"
            // Quando quasi tutti gli indici poggiano su una misura sola, la
            // media è fragile e va detto: basta un HRV in più per spostare un
            // atleta di cinquanta punti.
            sublabel={coperturaRecupero()}
            percentage={query.data?.totalRecovery ?? 0}
            labelCircle={query.data?.totalRecovery ?? undefined}
          />
        </GridItem>
        <GridItem colSpan={3}>
          <TopCard
            creatingPdf={creatingPdf}
            isLoading={query.isPending}
            Icon={WarningIcon}
            label="Stress"
            // Alto = brutta notizia: l'anello non deve essere verde come quello
            // del Recupero.
            tone="inverted"
            sublabel={copertura(query.data?.coverage?.stress)}
            percentage={query.data?.totalStress ?? 0}
            labelCircle={query.data?.totalStress ?? undefined}
          />
        </GridItem>
      </Grid>
    );
  };

  const renderThridRow = () => {
    return (
      <Grid
        templateRows="repeat(1, 1fr)"
        templateColumns="repeat(2, 1fr)"
        gap={4}
      >
        {/* <GridItem colSpan={1}>
          <AreaChart
            style={{
              width: "100%",
              maxWidth: "700px",
              maxHeight: "70vh",
              aspectRatio: 1.618,
            }}
            responsive
            data={data}
            margin={{
              top: 20,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis width="auto" />
            <Tooltip />
            <Area
              type="monotone"
              dataKey="uv"
              stroke="#05749A"
              fill="#05749A80"
            />
            <RechartsDevtools />
          </AreaChart>
        </GridItem> */}
        <GridItem colSpan={1}>
          <StressChart query={query} />
        </GridItem>
      </Grid>
    );
  };

  return (
    <div>
      <Grid
        templateRows="repeat(1, 1fr)"
        templateColumns="repeat(1, 1fr)"
        gap={4}
      >
        {/* Hub cliccabile dei moduli recenti (#18): colpo d'occhio + scorciatoie
            ai tab di dettaglio, sopra la panoramica a 14 giorni. Gli heading di
            sezione distinguono i due gruppi (periodi diversi). */}
        {onGoTo && (
          <>
            <GridItem colSpan={1}>
              <Typhography type="h3" size="bold-14" className={style.sectionTitle}>Colpo d&apos;occhio</Typhography>
            </GridItem>
            <GridItem colSpan={1}>
              <OverviewHub hub={hub} onGoTo={onGoTo} />
            </GridItem>
          </>
        )}

        <GridItem colSpan={1}>
          <Typhography type="h3" size="bold-14" className={style.sectionTitle}>Medie · ultime 2 settimane</Typhography>
        </GridItem>
        <GridItem colSpan={1}>{renderFirstRow()}</GridItem>

        {/* Card disponibilità — conteggi dal backend, non dedotti qui */}
        <GridItem colSpan={1}>
          <AvailabilityCard teamId={params.id} injuriesQuery={injuriesQuery} />
        </GridItem>

        <GridItem colSpan={1}>
          {
            <SecondRow
              query={query}
              teamQuery={teamQuery}
              creatingPdf={creatingPdf}
            />
          }
        </GridItem>
        <GridItem colSpan={1}>
          <TablesRow query={query} teamQuery={teamQuery} />
        </GridItem>
        <GridItem colSpan={1}>{renderThridRow()}</GridItem>
      </Grid>
    </div>
  );
};

export default InformationsTab;
